import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Heart, Send } from "lucide-react";
import workvivoInboundKudosService from "../services/workvivoInboundKudosService";
import UserAvatar from "../components/UserAvatar";

const statusLabels = {
  prompt_sent: "Ready to convert",
  deleted: "Deleted in Workvivo",
  converted: "Converted",
};

const Person = ({ person, label }) => (
  <div className="flex items-center gap-3 min-w-0">
    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
      <UserAvatar src={person?.avatar_url} name={person?.full_name || person?.email} className="w-full h-full object-cover" textClassName="text-sm" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900 truncate">
        {person?.full_name || person?.email || "Unknown user"}
      </p>
      {person?.email && <p className="text-xs text-slate-500 truncate">{person.email}</p>}
    </div>
  </div>
);

const WorkvivoKudoPage = () => {
  const { suggestionId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["workvivo-inbound-kudo", suggestionId],
    queryFn: () => workvivoInboundKudosService.getSuggestion(suggestionId),
    enabled: !!suggestionId,
  });

  const convertUrl = `/recognition?workvivoKudoSuggestionId=${suggestionId}`;
  const isConsumed = data?.status === "deleted" || data?.status === "converted";

  if (isLoading) {
    return <div className="max-w-3xl mx-auto p-6 text-slate-500">Loading...</div>;
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="border border-red-100 bg-red-50 rounded-lg p-5 text-red-700">
          Unable to load this Workvivo kudo.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-5">
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-pink-600 mb-2">
            <Heart size={18} />
            Workvivo kudo
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Review kudo</h1>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isConsumed ? "bg-slate-100 text-slate-600" : "bg-green-50 text-green-700"}`}>
          {statusLabels[data?.status] || data?.status}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-sm">
        <div className="grid sm:grid-cols-2 gap-4 pb-5 border-b border-slate-100">
          <Person person={data?.sender} label="From" />
          <Person person={data?.recipient} label="To" />
        </div>

        <div className="py-6">
          <p className="text-slate-800 whitespace-pre-wrap leading-relaxed">
            {data?.message || "No message was included with this kudo."}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-5 border-t border-slate-100">
          <div className="text-xs text-slate-500">
            Workvivo kudo ID: <span className="font-mono">{data?.workvivo_kudo_id}</span>
          </div>
          <div className="flex items-center gap-2">
            {data?.workvivo_url && (
              <a
                href={data.workvivo_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <ExternalLink size={16} />
                Open in Workvivo
              </a>
            )}
            <button
              type="button"
              onClick={() => navigate(convertUrl)}
              disabled={isConsumed}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={16} />
              Send recognition
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkvivoKudoPage;
